/**
 * Avatar — circular user picture with initials fallback.
 *
 * Props:
 *   user: { name, email, avatar_url }  OR  null
 *   size: "xs" | "sm" | "md" | "lg"    (default "md")
 *   className: extra classes
 *
 * When `avatar_url` is present we render the image; otherwise we render a
 * coloured monogram derived from the user's name/email so the gallery still
 * feels personal.
 */

const SIZE_MAP = {
  xs: "w-6 h-6 text-[10px]",
  sm: "w-8 h-8 text-xs",
  md: "w-10 h-10 text-sm",
  lg: "w-20 h-20 text-2xl",
};

const PALETTE = [
  "#1FB6C1", "#A78BFA", "#F59E0B", "#10B981", "#3B82F6",
  "#F43F5E", "#06B6D4", "#EAB308", "#6366F1", "#FB923C",
];

function seedColor(key = "") {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function initialsOf(name = "", email = "") {
  const base = (name || email || "?").trim();
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

export default function Avatar({ user, size = "md", className = "" }) {
  const sizeCls = SIZE_MAP[size] || SIZE_MAP.md;
  const name = user?.name || user?.email || "?";
  const url = user?.avatar_url;
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        data-testid="avatar-img"
        className={`${sizeCls} rounded-full object-cover border border-white/10 shrink-0 ${className}`}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    );
  }
  const color = seedColor(user?.id || user?.email || name);
  return (
    <div
      data-testid="avatar-initials"
      className={`${sizeCls} rounded-full flex items-center justify-center font-bold tracking-wider text-white border border-white/10 shrink-0 ${className}`}
      style={{ background: color }}
      title={name}
    >
      {initialsOf(user?.name, user?.email)}
    </div>
  );
}
